import {
  DropdownEntry,
  DropdownMenu,
  DropdownMenuProps,
  DropdownRadioGroup,
} from '@design';
import { groupBy } from '@helpers';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  defaultDropdownKeys,
  menuItems,
  navigableDropdownOptions,
  TableAction,
} from '../screener-table-utils';
import styles from './TableDropdownMenu.module.scss';

export type TableDropdownMenuProps = {
  alignment?: 'start' | 'center' | 'end';
  handleAction: (action: TableAction) => void;
  disabledKeys?: TableAction[];
  enabledKeys?: TableAction[];
  selectedKey?: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  filterType?: 'screening' | 'data' | 'default';
  activeFilterDescription?: string;
  renderTriggerNode: (hasNavigableOption: boolean) => ReactNode;
  triggerProps?: DropdownMenuProps['triggerProps'];
};

export const TableDropdownMenu = ({
  alignment = 'end',
  handleAction,
  disabledKeys = [],
  enabledKeys = defaultDropdownKeys,
  onOpenChange,
  open,
  renderTriggerNode,
  filterType = 'default',
  activeFilterDescription,
  selectedKey,
  triggerProps,
}: TableDropdownMenuProps) => {
  const { t } = useTranslation(['common']);

  const mappedItems = menuItems
    .filter(item => enabledKeys.includes(item.key))
    .filter(item => !disabledKeys.includes(item.key))
    .map(item => ({
      ...item,
      /* i18next-extract-mark-context-next-line ["ascending", "descending", "filter", "pin", "unpin", "left", "right", "start", "end", "clearFilter", "editFilter", "themeOverview"] */
      label: (
        <div className={styles.dropdownLabelContainer}>
          {t('common:table.dropdown', {
            context: item.key,
            /* i18next-extract-mark-context-next-line ["data", "screening", "default"] */
            filterType: t('common:table.filterType', {
              context: filterType,
            }),
          })}
          <span className={styles.dropdownLabelSubtitle}>
            {item.key === 'editFilter' && activeFilterDescription}
          </span>
        </div>
      ),
      onClick: () => handleAction(item.key),
    }));

  const hasNavigableOption = mappedItems.some(item =>
    navigableDropdownOptions.includes(item.key)
  );

  const groups: DropdownEntry[][] = Object.values(
    groupBy(mappedItems, item => item.group)
  );

  return (
    <DropdownMenu
      align={alignment}
      onOpenChange={onOpenChange}
      open={open}
      triggerNode={renderTriggerNode(hasNavigableOption)}
      triggerProps={triggerProps}
    >
      {groups.map((group, index) => (
        <DropdownRadioGroup
          key={index}
          items={group}
          selectedKey={selectedKey}
        />
      ))}
    </DropdownMenu>
  );
};
